import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import UserInfo from "../../../components/UserInfo";
import SabhaSaarHeader from "../../../components/SabhaSaarHeader";
import TopHeader from "../../../components/TopHeader";
import Wrapper from "../../../assets/wrappers/draftMeetingSingle.js";
import { toast } from "react-toastify";
import { marked } from "marked";
import Footer from "../../../components/Footer";
import protectedAPI from "../../../services/axiosInstance";
import { stopGlobalAudio } from "../../../utils/audioManager";
import DraftMeetingSummary from "./DraftMeetingSummary";
import { regionalLanguages, setIsDirty } from "../../../features/upload/uploadSlice"
import { useDispatch, useSelector } from "react-redux";
import { FaTrashAlt, FaCheck } from "react-icons/fa";
import { RootState } from "../../../store";
import DiscardDraftModal from "./DiscardDraftModal";
const IoArrowBack = require("react-icons/io5").IoArrowBack;

const DraftMeetingSingle: React.FC = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const navigate = useNavigate();
    const location = useLocation();
    const { taskId } = useParams<{ taskId: string }>();
    const [data, setData] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [summaryHtml, setSummaryHtml] = useState({
        englishSummary: "",
        regionalSummary: ""
    });
    const { isDirty  } = useSelector(
        (state: RootState) => state.upload
    );
    const dispatch = useDispatch()
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!taskId) return;
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await protectedAPI.get(`${baseUrl}api/draft-details/${taskId}`);
                setData(response.data);
            } catch (err: any) {
                setError("Failed to fetch meeting details");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [taskId]);

    const normalizeMarkdown = (text: string) => {
        return text.replace(/\*\*\s+</g, "**<").replace(/>\s+\*\規/g, ">**");
    };

    useEffect(() => {
        const convertSummary = async () => {
            if (data?.english_minutes || data?.translated_minutes) {
                const regionalHtml = data?.translated_minutes ? await marked.parse(normalizeMarkdown(data.translated_minutes)) : "";
                const englishHtml = data?.english_minutes ? await marked.parse(normalizeMarkdown(data.english_minutes)) : "";
                setSummaryHtml({
                    englishSummary: englishHtml,
                    regionalSummary: regionalHtml,
                });
            }
        };
        convertSummary();
    }, [data?.english_minutes, data?.translated_minutes]);

    const handleSaveSummary = async (updatedHtml: string, type: "english" | "regional") => {
        let targetLanguageCode = "en";
        if (type === "regional") {
            const rawLangLabel = data?.translated_minutes_lang || "";
            const matchedLang = regionalLanguages.find(
                (lang) => lang.label.toLowerCase() === rawLangLabel.trim().toLowerCase()
            );
            targetLanguageCode = matchedLang ? matchedLang.code : rawLangLabel.toLowerCase() || "regional";
        }

        try {
            const payload = {
                task_id: taskId,
                target_language: targetLanguageCode,
                edited_minutes: updatedHtml,
            };

            const res = await protectedAPI.post(
                `${baseUrl}api/submit-edited-summary`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            const responseData = res.data;

            if (responseData?.msg_code === 200) {
                setSummaryHtml(prev => ({
                    ...prev,
                    [type === "english" ? "englishSummary" : "regionalSummary"]: updatedHtml
                }));

                toast.success(responseData.msg || "Changes submitted successfully");
            } else {
                toast.error(responseData?.msg || "Something went wrong while submitting.");
                throw new Error(responseData?.msg || "Server validation error encountered.");
            }
        } catch (error: any) {
            console.error("Error submitting edited summary:", error);
            toast.error(
                error?.response?.data?.msg ||
                "Failed to submit changes. Please try again."
            );
            throw error;
        }
    };

    const handleSubmitMinutes = async () => {
        const confirmSubmit = window.confirm(
            "Are you sure you want to final submit the Minutes of Meeting?\n\nOnce submitted, it cannot be edited later on."
        );
        if (!confirmSubmit) {
            toast.info("Final submission cancelled.");
            return;
        }
        try {
            const payload = {
                task_id: taskId,
            };
            const res = await protectedAPI.post(
                `${baseUrl}api/submit-minutes`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = res.data;

            if (data?.msg_code === 200) {
                dispatch(setIsDirty(true));
                toast.success("Minutes saved successfully.");
            } else {
                toast.error(data?.msg || "Something went wrong while submitting.");
            }
        } catch (error) {
            console.error("Error submitting minutes:", error);
            toast.error("Failed to submit. Please try again.");
        }
    };

    const handleBackNavigation = () => {
        const currentPath = location.pathname;
        const shouldShowConfirm = (currentPath === "/processing" || currentPath.startsWith("/draft-meeting/")) && !isDirty;
        if (shouldShowConfirm) {
            const confirmed = window.confirm(
                "You have unsubmitted changes in your SabhaSaar MoM data. Are you sure you want to leave?"
            );

            if (!confirmed) return;
        }
        navigate("/draftMeetingsDetails");
        stopGlobalAudio();
    };

    const handleDiscardDraft = async () => {
        try {
            const payload = {
                task_id: taskId,
            };
            const res = await protectedAPI.post(
                `${baseUrl}api/discard-draft`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = res.data;

            if (data?.msg_code === 200) {
                toast.success(data?.msg || "Draft discarded successfully");
                navigate("/draftMeetingsDetails");
            } else {
                toast.error(
                    data?.msg || "Something went wrong while discarding the draft."
                );
            }
        } catch (error) {
            console.error("Error discarding draft:", error);
            toast.error("Failed to discard the draft. Please try again.");
        } finally {
            setIsModalOpen(false);
        }
    };

    return (
        <Wrapper>
            <TopHeader />
            <section className="body-content">
                <SabhaSaarHeader />
                <UserInfo />
                <div className="container" id="uploadMainSkip">
                    <div className="card mt-3">
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <h5 className="card-title-cus">
                                    View Draft Meetings Details
                                </h5>
                                <div
                                    className="goBack"
                                    onClick={handleBackNavigation}
                                >
                                    <IoArrowBack size={24} /> Back
                                </div>
                            </div>
                            <div className="">
                                {data?.meeting_type && <p className="mb-0"> <b>Meeting Type : </b>{data.meeting_type}</p>}
                                {data?.meeting_title && <p> <b>Meeting Title :</b> {data.meeting_title}</p>}
                            </div>
                            {loading && <p className="text-center">Loading...</p>}
                            {error && <p className="text-danger">Error Occurred !!!</p>}
                            {data?.media_type && data?.video_url && (
                                <div className="preview-area mt-3">
                                    {data.media_type === "video" ? (
                                        <video width="100%" height="auto" controls>
                                            <source src={data.video_url} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : data.media_type === "audio" ? (
                                        <audio controls>
                                            <source src={data.video_url} type="audio/mpeg" />
                                            Your browser does not support the audio tag.
                                        </audio>
                                    ) : null}
                                </div>
                            )}
                        </div>
                        <div className="card">
                            <div className="card-body">
                                <h4 className="text-center">Final Results Section</h4>

                                {summaryHtml.englishSummary && (
                                    <DraftMeetingSummary
                                        title="View Minutes of the Meeting (English)"
                                        htmlContent={summaryHtml.englishSummary}
                                        rawContent={data?.english_minutes || ""}
                                        isOpen={openIndex === 0}
                                        onToggle={() => setOpenIndex(openIndex === 0 ? null : 0)}
                                        onSave={(updatedHtml: any) => handleSaveSummary(updatedHtml, "english")}
                                    />
                                )}

                                {summaryHtml.regionalSummary && (
                                    <DraftMeetingSummary
                                        title={`View Minutes of the Meeting (${data?.translated_minutes_lang || "Regional"})`}
                                        htmlContent={summaryHtml.regionalSummary}
                                        rawContent={data?.translated_minutes || ""}
                                        isOpen={openIndex === 1}
                                        onToggle={() => setOpenIndex(openIndex === 1 ? null : 1)}
                                        onSave={(updatedHtml: any) => handleSaveSummary(updatedHtml, "regional")}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-center gap-3 m-3">
                        <button className="btn btn-danger d-flex align-items-center gap-2" onClick={() => { setIsModalOpen(true) }}>
                            <FaTrashAlt size={14} />  Discard Draft
                        </button>

                        <button className="btn btn-success d-flex align-items-center gap-2" onClick={handleSubmitMinutes}>
                            <FaCheck size={14} /> Final Submit
                        </button>
                    </div>

                    <div className="card bg-danger-cus mt-2">
                        <div className="card-body">
                            <h5 className="card-heading text-center text-danger">DISCLAIMER</h5>
                            <p>
                                The AI-generated summaries are based on uploaded audio/video and may contain errors due to input quality or language variation. Please verify the content before official use. The Ministry of Panchayati Raj is not liable for any discrepancies.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />

            <DiscardDraftModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDiscardDraft}
            />

        </Wrapper>
    );
};

export default DraftMeetingSingle; 